import { useEffect, useMemo, useState } from 'react';

/**
 * Headless version of AdminTable: takes the same rows/search/filter/sort/page
 * props and returns the resolved page slice plus the toolbar state. Pages
 * that need richer per-row markup than a single `<tr>` (e.g. PropertiesAdmin
 * with its expandable parcel sub-tables) can render their own list while
 * still benefiting from shared search + chip filters + pagination.
 *
 * Filter groups follow the same { id, label, options, selected, onChange,
 * multi? } shape that AdminTable accepts so toolbars stay consistent
 * across sections.
 */
export default function useAdminListState({
  rows = [],
  searchKeys,
  searchPredicate,
  filterGroups = [],
  defaultSort = null,
  defaultPageSize = 25,
}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(defaultSort);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const filterSignature = JSON.stringify(filterGroups.map((g) => g.selected || []));

  const filtered = useMemo(() => {
    let out = Array.isArray(rows) ? [...rows] : [];
    const q = search.trim().toLowerCase();
    if (q) {
      if (typeof searchPredicate === 'function') {
        out = out.filter((row) => searchPredicate(row, q));
      } else if (Array.isArray(searchKeys) && searchKeys.length > 0) {
        out = out.filter((row) =>
          searchKeys.some((k) => String(row?.[k] ?? '').toLowerCase().includes(q))
        );
      }
    }
    if (sort && typeof sort.getValue === 'function') {
      out.sort((a, b) => {
        const av = sort.getValue(a);
        const bv = sort.getValue(b);
        if (av == null && bv == null) return 0;
        if (av == null) return -1;
        if (bv == null) return 1;
        let cmp;
        if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sort.direction === 'desc' ? -cmp : cmp;
      });
    }
    return out;
  }, [rows, search, sort, searchKeys, searchPredicate]);

  const total = filtered.length;
  const isAllPage = pageSize === 'all';
  const effectiveSize = isAllPage ? Math.max(total, 1) : Number(pageSize) || defaultPageSize;
  const pageCount = Math.max(1, Math.ceil(total / effectiveSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    setPage(1);
  }, [search, filterSignature, pageSize]);

  const visible = useMemo(() => {
    if (isAllPage) return filtered;
    const start = (page - 1) * effectiveSize;
    return filtered.slice(start, start + effectiveSize);
  }, [filtered, page, effectiveSize, isAllPage]);

  return {
    search, setSearch,
    sort, setSort,
    page, setPage,
    pageSize, setPageSize,
    pageCount,
    total,
    filtered,
    visible,
    showingFrom: total === 0 ? 0 : (isAllPage ? 1 : (page - 1) * effectiveSize + 1),
    showingTo: isAllPage ? total : Math.min(page * effectiveSize, total),
  };
}
